import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MobSplash } from '../../components/mob';
import { sectionService } from '../../api/services/sectionService';
import { tokenStore } from '../../auth/tokenStore';
import { authEvents } from '../../auth/authEvents';
import { isChoosable, sectionChoice } from '../../domain/sections';
import type { SectionDto } from '../../api/types/dine.types';
import './LoginScreen.css';

/**
 * The waiter's section (spec 2.41 §12.1a). Sits after the outlet: when the
 * outlet has two or more sections, the waiter picks the one they work in; a
 * section without tables is shown but cannot be picked. With one section it
 * is chosen without asking, with none the whole floor shows. The board then
 * shows only that section's tables, and their bills go to its billing point.
 * Signing out clears the choice, as with the outlet.
 */
export default function SectionGate() {
  const qc = useQueryClient();
  const [state, setState] = useState<'checking' | 'choose' | 'ready'>(tokenStore.isSectionChosen() ? 'ready' : 'checking');
  const [sections, setSections] = useState<SectionDto[]>([]);

  useEffect(() => {
    if (state !== 'checking') return;
    let live = true;
    sectionService
      .list()
      .then((all) => {
        if (!live) return;
        const choice = sectionChoice(all);
        if (choice.kind === 'choose') {
          setSections(choice.sections);
          setState('choose');
          return;
        }
        tokenStore.setSection(choice.kind === 'auto' ? { id: choice.section.id, name: choice.section.sectionName } : 'none');
        setState('ready');
      })
      .catch(() => live && setState('ready')); // the screens report the API's reason
    return () => {
      live = false;
    };
  }, [state]);

  // A stored section (F5, a restored tab) is re-checked once: when it is no
  // longer one of the outlet's choosable sections, the waiter is asked again.
  // A failed check keeps it.
  useEffect(() => {
    const stored = tokenStore.getSectionId();
    if (!stored) return;
    let live = true;
    sectionService
      .list()
      .then((all) => {
        if (!live || tokenStore.getSectionId() !== stored) return;
        if (!all.some((s) => s.id === stored && isChoosable(s))) askAgain();
      })
      .catch(() => {
        // keep the stored section; the board still loads
      });
    return () => {
      live = false;
    };
    // Once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The board found none of the section's tables: ask again.
  useEffect(() => authEvents.on('sectionCleared', askAgain));

  function askAgain() {
    tokenStore.setSection(null);
    qc.removeQueries({ queryKey: ['tables'] });
    setState('checking');
  }

  if (state === 'checking') return <MobSplash />;

  if (state === 'choose') {
    return (
      <div className="login wtr-body">
        <div className="login__col">
          <div className="login__brand">
            <div className="login__name">DineOps</div>
            <div className="login__sub">Waiter</div>
          </div>
          <div className="login__picker" role="region" aria-label="Choose your section" data-section-picker>
            <div className="wtr-grp" style={{ marginTop: 0 }}>
              Choose your section
            </div>
            {sections.map((s) => {
              const ok = isChoosable(s);
              return (
                <button
                  key={s.id}
                  type="button"
                  className={ok ? 'wtr-opt' : 'wtr-opt wtr-opt--off'}
                  disabled={!ok}
                  onClick={() => {
                    qc.removeQueries({ queryKey: ['tables'] });
                    tokenStore.setSection({ id: s.id, name: s.sectionName });
                    setState('ready');
                  }}
                >
                  <span className="wtr-opt__nm">
                    {s.sectionName}
                    <span className="login__picker-app">
                      {' '}
                      · {ok ? `${s.tableCount} table${s.tableCount === 1 ? '' : 's'}` : 'no tables yet'}
                      {s.billingPointName ? ` · bills at ${s.billingPointName}` : ''}
                    </span>
                  </span>
                </button>
              );
            })}
            <div className="login__sub" style={{ marginTop: 8 }}>
              To change section later, sign out and sign in again.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
